import { useLanguage } from "@utils/hooks/useLanguage";
import { useEffect, useRef, useState, useMemo } from "react";
import ScoreWithLabelImageBg from "@components/StatsConsole/Widgets/StatsWidget/parts/GoalRowImageBg/ScoreWithLabelImageBg";
import { useColorContext } from "@stores/ColorContext";
import { useTheme } from "@stores/ThemeContext";
import darkGoal from "@assets/dynamicColors/goalNet/dark-goal.png";
import lightGoal from "@assets/dynamicColors/goalNet/light.png";

const CONSTANTS = {
    NET_ROWS: 4,
    SQUARE_WIDTH: 16.5,
    INITIAL_NET_SQUARES: 12
} as const;

interface GoalRowProps {
    homeShotsOnTarget: number;
    homeShotsOffTarget: number;
    awayShotsOnTarget: number;
    awayShotsOffTarget: number;
}

const GoalRowImageBg = ({ homeShotsOnTarget, homeShotsOffTarget, awayShotsOnTarget, awayShotsOffTarget }: GoalRowProps) => {
    const goalRowRef = useRef<HTMLDivElement>(null);
    const [netSquareQuantity, setNetSquareQuantity] = useState<number>(CONSTANTS.INITIAL_NET_SQUARES);
    const { colors, isDynamicColors } = useColorContext();
    const { theme } = useTheme();

    const calculatePercent = (value: number, total: number): number => {
        if (total === 0) return 50;
        return Math.round((value / total) * 100);
    };

    const percentages = useMemo(() => ({
        onTarget: {
            home: calculatePercent(homeShotsOnTarget, homeShotsOnTarget + awayShotsOnTarget),
            away: calculatePercent(awayShotsOnTarget, homeShotsOnTarget + awayShotsOnTarget)
        },
        offTarget: {
            home: calculatePercent(homeShotsOffTarget, homeShotsOffTarget + awayShotsOffTarget),
            away: calculatePercent(awayShotsOffTarget, homeShotsOffTarget + awayShotsOffTarget)
        }
    }), [homeShotsOnTarget, awayShotsOnTarget, homeShotsOffTarget, awayShotsOffTarget]);

    const language = useLanguage();

    //Choose the appropriate image based on the theme
    const backgroundImage = theme === 'dark' ? darkGoal : lightGoal;

    return (
        <div ref={goalRowRef} 
            data-testid="goal-row-container"
            className={`relative flex flex-row w-full h-[187px] mt-[24px] bg-transparent overflow-hidden`}>
            
            {/* Background with the goal image */}
            <div 
                className="absolute inset-0 w-full h-full flex justify-center"
            >
                <div 
                    className="w-full max-w-[500px] h-[187px] max-h-[187px] bg-center bg-no-repeat"
                    style={{
                        backgroundImage: `url(${backgroundImage})`,
                        backgroundSize: '100% 100%'
                    }}
                />
            </div>
            
            {/* Content with results and labels */}
            <div 
                data-testid="goal-row-content"
                className="absolute left-[0%] top-[0%] w-[100%] h-[100%] flex flex-col items-start gap-[49px] mt-[12px] z-10">
                <ScoreWithLabelImageBg
                    label={"InHouseStats.ShotsOffTarget"}
                    home={homeShotsOffTarget}
                    away={awayShotsOffTarget}
                    isTwoRows={false}
                />
                <ScoreWithLabelImageBg
                    label={"InHouseStats.ShotsOnTarget"}
                    home={homeShotsOnTarget}
                    away={awayShotsOnTarget}
                    isTwoRows={true}
                />
            </div>
        </div>
    )
}

export default GoalRowImageBg;
